import type { ReactNode } from 'react'
import { memo, useMemo, useState } from 'react'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import { BaseModal } from '../BaseModal'
import type { FlowStatus } from '@/apis/flow'
import { getFlowExecuteLog } from '@/apis/flow'
import { Button } from '@/components'
import type { LogItem } from '@/apis/run'
import { RunTag } from './components/RunTag'
import { StyledSegmented } from './components/StyledSegmented'
import { LoggerModalProvider } from './context'
import { LoggerDetail } from './components/Detail'
import { LoggerInput } from './components/Input'
import { LoggerOutput } from './components/Output'
import { LoggerLog } from './components/Log'
import type { LoggerTab } from './type'

const tabsMap: Array<{ value: LoggerTab; label: string }> = [
  {
    value: 'detail',
    label: '详情',
  },
  {
    value: 'input',
    label: '输入',
  },
  {
    value: 'output',
    label: '输出',
  },
  {
    value: 'log',
    label: '日志',
  },
]

export interface LoggerProps {
  title?: ReactNode
  status?: 'success' | 'error'
  mode?: 'log' | 'debug' | 'diff-debug'
  tabs?: LoggerTab[]
  defaultTab?: LoggerTab
  hideRunButton?: boolean
  flowId?: string
  taskId?: string
  onClose?: () => void
  onTabChange?: (tab: LoggerTab) => void
  onRun?: () => void
  onLogReRun?: (flowLog: LogItem) => void
  onLogTabChange?: (key: string, open: boolean) => void
}

export function LoggerTitle({
  time,
  status,
}: {
  time?: string
  status?: FlowStatus
}) {
  return (
    <div className='flex items-center'>
      <div className='m-r-12 text-16px font-600 c-font'>
        日志 {dayjs(time).format('YYYY-MM-DD HH:mm:ss')}
      </div>
      {status && <RunTag status={status} />}
    </div>
  )
}

export const LoggerContent: React.FC<Omit<LoggerProps, 'onClose' | 'title'>> =
  memo(props => {
    const {
      taskId = '',
      flowId = '',
      tabs = ['detail', 'input', 'output', 'log'],
      defaultTab = 'detail',
      mode = 'log',
      hideRunButton = false,
      // onClose,
      onLogReRun,
    } = props

    const { data: flowLog } = useRequest(
      () =>
        getFlowExecuteLog({
          flowId,
          taskId,
        }),
      { ready: !!flowId && !!taskId, refreshDeps: [flowId, taskId] },
    )

    const [activeTab, setActiveTab] = useState<LoggerTab>(defaultTab)

    const _tabs = useMemo(() => {
      return tabsMap.filter(v => tabs?.includes(v.value))
    }, [tabs])

    const runButton = useMemo<{
      text: string
      event?: (flowLog: LogItem) => void
    }>(() => {
      const isLogMode = mode === 'log'
      const isLoading = false
      const isInputActive = activeTab === 'input'

      if (isLogMode) {
        return {
          text: '重新运行',
          event: onLogReRun,
        }
      } else if (isLoading) {
        return {
          text: '运行中',
        }
      } else if (isInputActive) {
        return {
          text: '开始运行',
        }
      } else {
        return {
          text: '开始运行',
        }
      }
    }, [activeTab, mode])

    return (
      <LoggerModalProvider
        flowLog={flowLog}
        mode={mode}
        switchTab={setActiveTab}
      >
        <div className='h-full flex flex-col overflow-hidden'>
          <div className='p-16px pb-0'>
            <StyledSegmented
              block
              defaultValue={activeTab}
              options={_tabs}
              onChange={tab => setActiveTab(tab as unknown as LoggerTab)}
            />
          </div>
          <div className='flex-1  overflow-hidden p-16px'>
            {activeTab === 'detail' && <LoggerDetail />}
            {activeTab === 'input' && <LoggerInput />}
            {activeTab === 'output' && <LoggerOutput />}
            {activeTab === 'log' && <LoggerLog />}
          </div>
          {!hideRunButton && (
            <div className='w-full p-20px h-80px  b-t-1 b-t-line b-t-op-60'>
              <Button
                className='w-full'
                type='primary'
                onClick={() => runButton?.event?.(flowLog as LogItem)}
              >
                {runButton.text}
              </Button>
            </div>
          )}
        </div>
      </LoggerModalProvider>
    )
  })

export const NewLoggerModal: React.FC<LoggerProps> = memo(props => {
  const { onClose, title } = props

  return (
    <BaseModal onClose={onClose} title={title}>
      <LoggerContent {...props} />
    </BaseModal>
  )
})
